import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AddButton from '../../components/IconButton'
import addIcon from '../../assets/icons/Addimg.svg'
import ViewServices from './ViewServices'
import ArtisansPage from './ViewServiceList'
import ViewServicesRequest from '../ServiceRequest/ViewServicesRequest'
import AddServiceModal from '../../components/Modal/Services/AddServiceModal'
import ArtisanService from '../../api/artisan-services'
import Metadata from '../../components/Metadata'
import PageWrapper from '../../components/PageWrapper'

type LocalService = { id: string; type: string; description?: string }

type ServiceApi = {
  id?: string
  _id?: string
  serviceId?: string
  title?: string
  name?: string
  type?: string
  description?: string
  community?: string
}

export default function Services(): JSX.Element {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalOpen, setModalOpen] = React.useState(false)
  const [estateId, setEstateId] = React.useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const currentView = searchParams.get('view')
  const serviceId = searchParams.get('serviceId') || ''

  React.useEffect(() => {
    const estateStr = localStorage.getItem('estate')
    if (estateStr) {
      try {
        const estate = JSON.parse(estateStr)
        setEstateId(estate?.id || '')
      } catch (error) {
        console.error('Error parsing estate from localStorage:', error)
        setEstateId('')
      }
    }
  }, [])

  const shouldFetch = estateId && estateId.trim().length > 0

  const {
    data: serviceData,
    mutate,
    isLoading: servicesLoading,
    error: servicesError,
  } = ArtisanService.useGetServiceList({
    estateId: shouldFetch ? estateId : undefined, 
    page: 1,
    size: 15,
  })

  const services = React.useMemo(() => {
    const results = (serviceData as any)?.results ?? []
    return results.map((s: ServiceApi) => ({
      id: s.id ?? s._id ?? s.serviceId ?? '',
      title: s.title ?? s.name ?? s.type ?? 'Unknown',
      description: s.description ?? '',
      icon: undefined, 
    }))
  }, [serviceData])

  const initialServiceOptions = React.useMemo(() => {
    if (!shouldFetch) return []

    const results = ((serviceData as any)?.results ?? []) as ServiceApi[]
    return results.map((s: ServiceApi) => ({
      id: s.id ?? s._id ?? s.serviceId ?? String(s.id ?? ''),
      title: s.title ?? s.name ?? s.type ?? 'Unknown',
      description: s.description ?? '',
      community: s.community ?? '',
    }))
  }, [serviceData, shouldFetch])

  const handleViewSelect = (id: string) => {
    setSearchParams({ view: 'artisans', serviceId: id })
  }

  const handleShowServiceRequests = () => {
    setSearchParams({ view: 'requests' })
  }

  const handleBack = () => {
    setSearchParams({})
  }
  const handleAddButtonClick = () => setModalOpen(true)

  const handleAddService = async () => {
    await mutate()
  }

  React.useEffect(() => {
    if (!shouldFetch && estateId !== '') {
      navigate('/estates')
    }
  }, [shouldFetch, estateId, navigate])

  const servicesMetadata = {
    title: 'Services - Safelto',
    description: 'Manage and view all services available in your estate. Add new services, view service requests, and manage artisans.',
    keywords: ['services', 'estate services', 'artisan services', 'service requests', 'property management'],
    image: '/assets/images/safelto-big.png',
    url: 'https://safelto.com/services',
    type: 'website' as 'website'
  }

  if (estateId === '') {
    return (
      <PageWrapper title="Services" description="View all Services">
        <div className="flex items-center justify-center min-h-[50vh] py-8 sm:py-20">
          <div className="text-center px-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-t-transparent border-gray-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600">
              Loading estate information...
            </p>
          </div>
        </div>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper title="Services" description="View all Services">
      <Metadata {...servicesMetadata} />
      {/* Header Actions - Stack on mobile, row on larger screens */}
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 py-3 sm:py-5 mb-4 sm:mb-6">
        <button
          type="button"
          onClick={handleAddButtonClick}
          className="p-0 w-full sm:w-auto"
        >
          <AddButton
            buttonStyle="w-full sm:w-[250px]"
            icon={
              <img src={addIcon} alt="Add icon" className="w-8 h-8 sm:w-10 sm:h-10" />
            }
            iconClassName="w-8 h-8 sm:w-10 sm:h-10"
          >
            <div className="flex flex-col justify-center gap-0.5 sm:gap-1">
              <p className="text-black font-medium text-left text-sm sm:text-base">
                Add new service
              </p>
              <p className="text-xs sm:text-sm font-normal text-gray-600 text-left">
                Add new service available
              </p>
            </div>
          </AddButton>
        </button>

        <div
          onClick={handleShowServiceRequests}
          className="flex flex-col justify-center sm:ml-auto w-full sm:w-auto bg-[#2125291A] px-4 sm:px-6 py-2.5 sm:py-3 cursor-pointer hover:bg-gray-200 transition rounded-lg text-center sm:text-left"
        >
          <p className="text-xs sm:text-sm text-black font-medium">
            View Service Requests
          </p>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="w-full overflow-x-hidden">
        {currentView === 'requests' ? (
          <ViewServicesRequest
            setNumOfServices={() => {}}
            handleViewSelect={() => {}}
            onBack={handleBack}
          />
        ) : currentView === 'artisans' ? (
          <ArtisansPage
            estateId={estateId}
            serviceId={serviceId}
            onBack={handleBack}
            isLoading={isLoading}
            initialServiceOptions={initialServiceOptions.map((s) => ({
              id: s.id,
              title: s.title,
            }))}
          />
        ) : (
          <ViewServices
            estateId={estateId}
            services={services}
            isLoading={servicesLoading}
            error={servicesError}
            onServiceClick={handleViewSelect}
          />
        )}
      </div>

      <AddServiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        estateId={estateId}
        onAdd={handleAddService}
      />
    </PageWrapper>
  )
}

